(ns minorodata.components.home-dashboard.action-area
  (:require
   ["lucide-react" :refer [BrainCircuitIcon CableIcon GitGraphIcon LayoutListIcon
                           ListChecksIcon PlusIcon Share2Icon SquareTerminalIcon]]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.components.connections.connector-picker :as connector-picker]
   [minorodata.components.home-dashboard.extracts :refer [connectors-count-card
                                                          db-connectors-count-card
                                                          extracts-count-card]]
   [minorodata.components.home-dashboard.transforms :refer [transforms-active-card
                                                            transforms-in-queue-stat
                                                            transforms-running-stat]]
   [minorodata.components.pipeline-editor.dag :refer [new-transform-form]]
   [minorodata.components.sql-console-modal.core :refer [toggle-console-modal]]
   [re-posh.core :as re-posh]
   [reitit.frontend.easy :as rt.easy]
   [ui.popover :as popover]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))

(defn track-action [action]
  (dispatch [:posthog/capture {:event (:overview/action-clicked posthog-events/events)
                               :props {:action action}}]))

(defn plus-label []
  [:div {:class "absolute -bottom-1.5 -right-1.5 p-0.5 bg-kleene-text-success text-white rounded-full"}
   [:> PlusIcon {:class "w-4 h-4" :stroke-width 2}]])

(defn action-row-content
  [{:keys [icon title description badge icon-class]}]
  [:<>
   [:div
    {:class
     (clsx
      (str
       "relative flex h-9 w-9 shrink-0 items-center justify-center "
       "rounded-lg [&>svg]:h-5 [&>svg]:w-5")
      icon-class)}
    icon
    badge]

   [:div {:class "min-w-0 flex-1"}
    [typography/h4 {:class "text-sm leading-tight"}
     title]
    [typography/p
     {:class "text-xs leading-snug text-kleene-text-secondary"}
     description]]])

(def action-row-class
  (str
   "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left "
   "cursor-pointer transition-colors "
   "hover:bg-kleene-neutral-04 "
   "dark:hover:bg-kleene-hover-background"))

(defn action-group [{:keys [title]} & children]
  (into
   [:section {:class "flex min-w-0 flex-col"}
    [typography/p
     {:class "mb-2 text-xs font-semibold text-kleene-text-secondary"}
     title]]
   children))

(defn add-source-row []
  [connector-picker/add-source-dialog
   {:trigger
    [:button {:type     "button"
              :class    action-row-class
              :on-click #(track-action "connect_source")}
     [action-row-content
      {:icon        [:> CableIcon]
       :icon-class  (str
                     "bg-extracts/20 "
                     "[&>svg]:text-extracts")
       :badge       [plus-label]
       :title       "Connect source"
       :description "Ingest data in your warehouse"}]]}])

(defn add-transform-row []
  [popover/popover-root {:on-open-change #(when % (track-action "create_transform"))}
   [popover/popover-trigger {:as-child true}
    [:button {:type "button" :class action-row-class}
     [action-row-content
      {:icon        [:> LayoutListIcon]
       :icon-class  (str
                     "bg-transforms/20 "
                     "[&>svg]:text-transforms")
       :badge       [plus-label]
       :title       "Transform"
       :description "Transform data with SQL"}]]]
   [popover/popover-content
    [typography/h4 {:class "mb-4"} "Create new transform"]
    [new-transform-form
     {:on-create
      #(dispatch
        [:pipeline-editor.events/create-and-open-transform
         {:group-id (:group-id %)
          :transform {:name (:name %)}}])}]]])

(defn analytics-models-row []
  [:a
   {:class    action-row-class
    :href     (rt.easy/href :analytics-models)
    :on-click #(track-action "analytics_models")}

   [action-row-content
    {:icon        [:> BrainCircuitIcon]
     :icon-class  (str
                   "bg-kleene-secondary-05 "
                   "[&>svg]:text-kleene-secondary-v2")
     :badge       [plus-label]
     :title       "Analytics models"
     :description "Utilise ML to analyse your data"}]])

(defn sql-console-row []
  [:button
   {:type     "button"
    :class    action-row-class
    :on-click #(do (track-action "sql_console")
                   (toggle-console-modal))}

   [action-row-content
    {:icon        [:> SquareTerminalIcon]
     :icon-class  (str
                   "bg-kleene-neutral-05 "
                   "dark:bg-kleene-dark-03 "
                   "[&>svg]:text-kleene-text-secondary")
     :title       "SQL Console"
     :description "Explore your warehouse with SQL"}]])

(defn pipelines-row []
  [:a
   {:class    action-row-class
    :href     (rt.easy/href :dag)
    :on-click #(track-action "pipelines")}

   [action-row-content
    {:icon        [:> Share2Icon]
     :icon-class  (str
                   "bg-kleene-neutral-05 "
                   "dark:bg-kleene-dark-03 "
                   "[&>svg]:text-kleene-text-secondary")
     :title       "Pipelines"
     :description "Visual representation of pipelines"}]])

(defn pipeline-editor-row []
  [:a
   {:class    action-row-class
    :href     (rt.easy/href :pipeline-editor)
    :on-click #(track-action "pipeline_editor")}

   [action-row-content
    {:icon        [:> GitGraphIcon]
     :icon-class  (str
                   "bg-kleene-neutral-05 "
                   "dark:bg-kleene-dark-03 "
                   "[&>svg]:text-kleene-text-secondary")
     :title       "Pipeline editor"
     :description "Safely update transforms"}]])

(defn logs-row []
  [:a
   {:class    action-row-class
    :href     (rt.easy/href :log)
    :on-click #(track-action "logs")}

   [action-row-content
    {:icon        [:> ListChecksIcon]
     :icon-class  (str
                   "bg-kleene-neutral-05 "
                   "dark:bg-kleene-dark-03 "
                   "[&>svg]:text-kleene-text-secondary")
     :title       "Logs"
     :description "Trace executions"}]])

(defn overview-active-group [db-connectors-enabled?]
  [:section {:class "flex min-w-0 flex-col"}
   [typography/p
    {:class "mb-2 text-xs font-semibold text-kleene-text-secondary"}
    "Active"]

   [:div
    {:class
     "grid grid-cols-2 gap-0 overflow-hidden rounded-md border border-kleene-layout-border bg-kleene-background"}

    (when db-connectors-enabled?
      [:<>
       [:div {:class "border-b border-r border-kleene-layout-border"}
        [connectors-count-card]]

       [:div {:class "border-b border-kleene-layout-border"}
        [db-connectors-count-card]]])

    [:div {:class "border-r border-kleene-layout-border"}
     [extracts-count-card]]

    [:div
     [transforms-active-card]]]

   [typography/p
    {:class "mb-2 mt-4 text-xs text-kleene-text-secondary"}
    "Transforms execution"]

   [:div
    {:class
     "grid grid-cols-2 gap-0 overflow-hidden rounded-md border border-kleene-layout-border bg-kleene-background"}

    [:div {:class "border-r border-kleene-layout-border"}
     [transforms-running-stat]]

    [:div
     [transforms-in-queue-stat]]]])

(defn overview-action-area []
  (let [analytics-models-enabled?
        @(re-posh/subscribe [:unleash/is-enabled "fe.analytics-models"])

        db-connectors-enabled?
        @(re-posh/subscribe [:unleash/is-enabled "fe.db-connectors"])]
    [:div
     {:class
      (str
       "mx-auto grid w-full max-w-6xl grid-cols-1 items-start "
       "gap-x-8 gap-y-8 md:grid-cols-2 "
       "lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(22rem,1.15fr)]")}

     [action-group {:title "Create"}
      [add-source-row]
      [add-transform-row]
      (when analytics-models-enabled?
        [analytics-models-row])]

     [action-group {:title "Manage"}
      [sql-console-row]
      [pipelines-row]
      [pipeline-editor-row]
      [logs-row]]

     [:div {:class "md:col-span-2 lg:col-span-1"}
      [overview-active-group db-connectors-enabled?]]]))
